import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Article as PolicyIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { pageVariants, sectionVariants, staggerContainer, staggerItem } from '../animations/variants';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import { ReadOnlyButton } from '../components/common/ReadOnlyButton';

const CMP_BRAND_COLOR = '#9B59B6';
const CMP_BRAND_HOVER = '#864AA0';
const CMP_BRAND_TINT = '#F4EAF7';

// Heuristic warning: if the policy name collides with a well-known AWS managed
// policy name, the user almost certainly meant to attach the AWS-managed
// version via the Permission Set's "AWS Managed Policies" section instead.
const AWS_MANAGED_NAMES = new Set([
  'AdministratorAccess',
  'PowerUserAccess',
  'ReadOnlyAccess',
  'IAMFullAccess',
  'AmazonS3ReadOnlyAccess',
  'AmazonS3FullAccess',
  'AmazonEC2ReadOnlyAccess',
  'AmazonEC2FullAccess',
  'Billing',
]);

const emptyDoc = '{\n  "Version": "2012-10-17",\n  "Statement": []\n}';

export default function CustomerManagedPolicies() {
  const isReadOnly = useReadOnlyMode();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', policy_document: emptyDoc });
  const [editorError, setEditorError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [propagateConfirm, setPropagateConfirm] = useState(null); // { usage, body }

  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteUsage, setDeleteUsage] = useState(null);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [rolloutPanel, setRolloutPanel] = useState(null); // policy id whose rollouts are shown

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.listCustomerManagedPolicies({
        page: page + 1,
        pageSize,
        search,
      });
      setRows(res.policies || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e?.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', description: '', policy_document: emptyDoc });
    setEditorError(null);
    setEditorOpen(true);
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setEditorError(null);
    try {
      const full = await apiService.getCustomerManagedPolicy(id);
      setForm({
        name: full.name,
        description: full.description || '',
        policy_document: full.policy_document,
      });
      setEditorOpen(true);
    } catch (e) {
      setError(e?.message || 'Failed to load policy');
    }
  };

  const submitEditor = async () => {
    setEditorError(null);
    setSaving(true);
    const body = {
      name: form.name,
      description: form.description,
      policy_document: form.policy_document,
    };
    try {
      if (editingId) {
        const usage = await apiService.getCustomerManagedPolicyUsage(editingId);
        if ((usage?.accounts || []).length > 0) {
          setPropagateConfirm({ usage, body });
          setSaving(false);
          return;
        }
        await apiService.updateCustomerManagedPolicy(editingId, body);
      } else {
        await apiService.createCustomerManagedPolicy(body);
      }
      setEditorOpen(false);
      reload();
    } catch (e) {
      setEditorError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmPropagate = async () => {
    setSaving(true);
    try {
      await apiService.updateCustomerManagedPolicy(editingId, propagateConfirm.body);
      setPropagateConfirm(null);
      setEditorOpen(false);
      reload();
    } catch (e) {
      setEditorError(e?.message || 'Update failed');
      setPropagateConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const startDelete = async (row) => {
    setDeleteCandidate(row);
    setDeleteTyped('');
    try {
      const usage = await apiService.getCustomerManagedPolicyUsage(row.id);
      setDeleteUsage(usage);
    } catch {
      setDeleteUsage(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await apiService.deleteCustomerManagedPolicy(deleteCandidate.id);
      setDeleteCandidate(null);
      setDeleteUsage(null);
      setDeleteTyped('');
      reload();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const collisionWarning = AWS_MANAGED_NAMES.has(form.name);

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  backgroundColor: CMP_BRAND_COLOR,
                  width: 48,
                  height: 48,
                }}
              >
                <PolicyIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.75rem',
                    color: '#253E66',
                  }}
                >
                  Customer Managed Policies
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  {total} {total === 1 ? 'policy' : 'policies'} in the library
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search policies..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 250,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F9FBFD',
                    borderRadius: '6px',
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    '& fieldset': { borderColor: '#E5E5E5' },
                    '&:hover fieldset': { borderColor: CMP_BRAND_COLOR },
                    '&.Mui-focused fieldset': { borderColor: CMP_BRAND_COLOR },
                  },
                }}
              />
              <Tooltip title="Refresh">
                <IconButton
                  onClick={reload}
                  disabled={loading}
                  sx={{
                    color: '#666666',
                    backgroundColor: '#F9FBFD',
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: '#F0F4F8',
                      borderColor: CMP_BRAND_COLOR,
                      color: CMP_BRAND_COLOR,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: CMP_BRAND_COLOR }} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
              <ReadOnlyButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreate}
                disabled={isReadOnly}
                sx={{
                  backgroundColor: CMP_BRAND_COLOR,
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': { backgroundColor: CMP_BRAND_HOVER },
                }}
              >
                New Policy
              </ReadOnlyButton>
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Library List */}
      <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            overflow: 'hidden',
            minHeight: '400px',
          }}
        >
          {loading && rows.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ color: CMP_BRAND_COLOR }} />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  margin: '0 auto 16px',
                  backgroundColor: CMP_BRAND_TINT,
                }}
              >
                <PolicyIcon sx={{ fontSize: 32, color: CMP_BRAND_COLOR }} />
              </Avatar>
              <Typography
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: '#253E66',
                  mb: 1,
                }}
              >
                {search ? 'No matching policies' : 'No customer-managed policies yet'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                }}
              >
                {search
                  ? 'Try a different search term.'
                  : 'Create a policy to reuse the same document across permission sets.'}
              </Typography>
            </Box>
          ) : (
            <>
              <Table sx={{ '& .MuiTableCell-root': { borderColor: '#E5E5E5' } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Description
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <motion.tbody variants={staggerContainer} initial="initial" animate="animate">
                  {rows.map((r) => (
                    <motion.tr
                      key={r.id}
                      variants={staggerItem}
                      style={{ display: 'table-row' }}
                    >
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              color: '#253E66',
                              fontSize: '0.95rem',
                            }}
                          >
                            {r.name}
                          </Typography>
                          <Tooltip title="View rollout history">
                            <Chip
                              size="small"
                              variant="outlined"
                              icon={<TimelineIcon sx={{ fontSize: 14 }} />}
                              label="rollouts"
                              clickable
                              onClick={() => setRolloutPanel(r.id)}
                              sx={{
                                fontFamily: 'Metropolis, sans-serif',
                                fontSize: '0.7rem',
                                height: 22,
                                borderColor: '#E5E5E5',
                                color: '#666666',
                                '&:hover': {
                                  borderColor: CMP_BRAND_COLOR,
                                  color: CMP_BRAND_COLOR,
                                  backgroundColor: CMP_BRAND_TINT,
                                },
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', fontSize: '0.85rem' }}>
                        {r.description || '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : 'Edit'}>
                          <span>
                            <IconButton size="small" onClick={() => openEdit(r.id)} disabled={isReadOnly} sx={{ color: '#666666', '&:hover': { color: CMP_BRAND_COLOR } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : 'Delete'}>
                          <span>
                            <IconButton size="small" onClick={() => startDelete(r)} disabled={isReadOnly} sx={{ color: '#666666', '&:hover': { color: '#E74C3C' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </motion.tr>
                  ))}
                </motion.tbody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: '1px solid #E5E5E5',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontFamily: 'Metropolis, sans-serif',
                  },
                }}
              />
            </>
          )}
        </Paper>
      </motion.div>

      {/* Editor */}
      <Dialog open={editorOpen} onClose={() => !saving && setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          {editingId ? 'Edit' : 'New'} Customer Managed Policy
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              helperText={`Becomes "Prism-${form.name || '<name>'}" in each target account`}
              fullWidth
              autoFocus={!editingId}
            />
            {collisionWarning && (
              <Alert severity="warning">
                <strong>{form.name}</strong> matches an AWS-managed policy name. If you wanted to attach
                that AWS-managed policy, use the "AWS Managed Policies" section on the permission set instead.
              </Alert>
            )}
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
            />
            <TextField
              label="Policy Document (JSON)"
              value={form.policy_document}
              onChange={(e) => setForm({ ...form, policy_document: e.target.value })}
              multiline
              minRows={14}
              maxRows={30}
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            {editorError && <Alert severity="error">{editorError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditorOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitEditor}
            disabled={saving || !form.name}
            sx={{
              backgroundColor: CMP_BRAND_COLOR,
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              px: 3,
              '&:hover': { backgroundColor: CMP_BRAND_HOVER },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Propagation confirmation */}
      <Dialog open={!!propagateConfirm} onClose={() => !saving && setPropagateConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          Propagate update
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
            This will update the policy in{' '}
            <strong>{propagateConfirm?.usage?.accounts?.length || 0}</strong> account(s).
          </Typography>
          {propagateConfirm?.usage?.permission_sets?.length > 0 && (
            <Typography sx={{ mt: 1, fontFamily: 'Metropolis, sans-serif', color: '#666666', fontSize: '0.875rem' }}>
              Affected permission sets:{' '}
              {propagateConfirm.usage.permission_sets.map((p) => p.name).join(', ')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPropagateConfirm(null)} disabled={saving} sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmPropagate}
            disabled={saving}
            sx={{
              backgroundColor: CMP_BRAND_COLOR,
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              '&:hover': { backgroundColor: CMP_BRAND_HOVER },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save and propagate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteCandidate}
        onClose={() => !deleting && (setDeleteCandidate(null), setDeleteUsage(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          Delete customer-managed policy
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontFamily: 'Metropolis, sans-serif' }}>
            This will detach the policy from {deleteUsage?.role_attachments || 0} role(s) in{' '}
            {deleteUsage?.accounts?.length || 0} account(s) and delete the IAM policy from each.
            This cannot be undone.
          </Typography>
          <TextField
            fullWidth
            label={`Type "${deleteCandidate?.name || ''}" to confirm`}
            value={deleteTyped}
            onChange={(e) => setDeleteTyped(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setDeleteCandidate(null); setDeleteUsage(null); }}
            disabled={deleting}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteTyped !== deleteCandidate?.name || deleting}
            onClick={confirmDelete}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rollout status panel */}
      <AnimatePresence>
        {!!rolloutPanel && (
          <RolloutPanel policyId={rolloutPanel} onClose={() => setRolloutPanel(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function RolloutPanel({ policyId, onClose }) {
  const [rollouts, setRollouts] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!policyId) return;
    setLoading(true);
    try {
      const r = await apiService.listCustomerManagedPolicyRollouts(policyId);
      setRollouts(Array.isArray(r) ? r : []);
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  useEffect(() => {
    if (!policyId) return undefined;
    reload();
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [policyId, reload]);

  const failed = rollouts.filter((r) => r.status === 'FAILED');
  const succeeded = rollouts.filter((r) => r.status === 'SUCCESS').length;
  const pending = rollouts.filter((r) => r.status === 'PENDING').length;

  const retry = async () => {
    try {
      await apiService.retryFailedCustomerManagedPolicyRollouts(policyId);
      reload();
    } catch { /* ignore */ }
  };

  const statusColor = (s) => {
    if (s === 'SUCCESS') return '#27AE60';
    if (s === 'FAILED') return '#E74C3C';
    if (s === 'PENDING') return '#F39C12';
    return '#666666';
  };

  return (
    <Dialog open={!!policyId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
        Rollout status
      </DialogTitle>
      <DialogContent>
        {/* Summary chips */}
        {rollouts.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              size="small"
              label={`${succeeded} success`}
              sx={{ backgroundColor: '#E8F5E9', color: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
            />
            {pending > 0 && (
              <Chip
                size="small"
                label={`${pending} pending`}
                sx={{ backgroundColor: '#FFF3E0', color: '#F39C12', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
              />
            )}
            {failed.length > 0 && (
              <Chip
                size="small"
                label={`${failed.length} failed`}
                sx={{ backgroundColor: '#FFEBEE', color: '#E74C3C', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
              />
            )}
          </Box>
        )}

        {loading && rollouts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: CMP_BRAND_COLOR }} />
          </Box>
        ) : rollouts.length === 0 ? (
          <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', textAlign: 'center', py: 4 }}>
            No rollouts recorded.
          </Typography>
        ) : (
          <Table size="small" sx={{ '& .MuiTableCell-root': { fontFamily: 'Metropolis, sans-serif' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Account</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Error</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Started</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rollouts.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{r.account_id}</TableCell>
                  <TableCell>{r.event_type}</TableCell>
                  <TableCell sx={{ color: statusColor(r.status), fontWeight: 600 }}>{r.status}</TableCell>
                  <TableCell sx={{ color: '#E74C3C', fontSize: '0.8rem' }}>{r.error_message || ''}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem', color: '#666666' }}>{new Date(r.started_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {failed.length > 0 && (
          <Button
            onClick={retry}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif', color: CMP_BRAND_COLOR }}
          >
            Retry {failed.length} failed
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
